import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkVariant,
  ETextInputAlignment,
  ETextInputTypes,
  ETextStyleVariant,
  EToastVariant,
  Heading,
  Link as InlineLink,
  Loader,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useToast } from '@/hooks/useToast';
import { postCodeVerification, postTextVerification } from '@/redux/modules/auth';
import { IFullUserInfo } from '@/services/types/user/user';

import { EPersonalInfoFields, TPersonalInfoValues } from '../ProfileEdit';

interface IPhoneNumberProps {
  userData: IFullUserInfo;
  control: Control<TPersonalInfoValues>;
  errors: FieldErrors<TPersonalInfoValues>;
  phoneCodeError: boolean;
  getValues: UseFormGetValues<TPersonalInfoValues>;
  setValue: UseFormSetValue<TPersonalInfoValues>;
  refreshPageUser: () => void;
  setPhoneCodeError: (val: boolean) => void;
}

export const PhoneNumber = ({
  userData,
  control,
  errors,
  phoneCodeError,
  getValues,
  setValue,
  refreshPageUser,
  setPhoneCodeError,
}: IPhoneNumberProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [phoneCode, setPhoneCode] = useState<string>('');
  const [isVerifyingPhone, setIsVerifyingPhone] = useState<boolean>(false);
  const [isFinalizingPhoneCode, setIsFinalizingPhoneCode] = useState<boolean>(false);

  const numberRefs = {
    pn1: useRef<HTMLInputElement>(null),
    pn2: useRef<HTMLInputElement>(null),
    pn3: useRef<HTMLInputElement>(null),
    pn4: useRef<HTMLInputElement>(null),
  };

  const shouldShowValidateAction = useMemo(() => {
    const hasPhoneNumber = userData.profile.phone;
    const isPhoneVerified = userData.verifications.phone;

    return !isPhoneVerified && Boolean(hasPhoneNumber) && !phoneCodeError;
  }, [phoneCodeError, userData.profile.phone, userData.verifications.phone]);

  const submitPhoneCode = async () => {
    const code =
      getValues().verifyPhoneNumber1 +
      getValues().verifyPhoneNumber2 +
      getValues().verifyPhoneNumber3 +
      getValues().verifyPhoneNumber4;

    setPhoneCode(code);
  };

  const resetCode = () => {
    setValue('verifyPhoneNumber1', '');
    setValue('verifyPhoneNumber2', '');
    setValue('verifyPhoneNumber3', '');
    setValue('verifyPhoneNumber4', '');
    setPhoneCode('');
  };

  const resendCode = async () => {
    try {
      setIsVerifyingPhone(true);
      await dispatch(postTextVerification(userData.profile.phone || ''));
      resetCode();

      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: 'Code resend',
          id: '/r5KQD',
        }),
      );
      setPhoneCodeError(false);
      setIsVerifyingPhone(false);
    } catch (error) {
      setIsVerifyingPhone(false);
      const errorMessage = error?.response?.error;

      if (error.response.code === 'UnsupportedCountryCode') {
        setPhoneCodeError(true);
      }

      resetCode();

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        errorMessage
          ? errorMessage
          : intl.formatMessage({
              defaultMessage: 'Oops, something went wrong. Try again later.',
              id: '/m4g4K',
            }),
      );
    }
  };

  useEffect(() => {
    let done = false;

    async function fetchData() {
      try {
        setIsFinalizingPhoneCode(true);

        await dispatch(postCodeVerification(phoneCode, userData.profile.phone || ''));
        if (done) {
          setPhoneCodeError(false);
          await refreshPageUser();

          showToast(
            EToastVariant.Success,
            intl.formatMessage({
              defaultMessage: 'Success',
              id: 'xrKHS6',
            }),
            intl.formatMessage({
              defaultMessage: 'Your phone number has been verified.',
              id: 'kQiQhp',
            }),
          );
        }
      } catch (error) {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({
            defaultMessage: 'Error',
            id: 'KN7zKn',
          }),
          error.response?.error
            ? error.response.error
            : error.error
              ? error.error
              : intl.formatMessage({
                  defaultMessage: 'Oops, something went wrong. Try again later.',
                  id: '/m4g4K',
                }),
        );
      } finally {
        resetCode();
        setIsFinalizingPhoneCode(false);
      }
    }

    if (phoneCode) {
      fetchData();
    }

    return () => {
      done = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCode]);

  return (
    <div className="flex flex-col gap-2" id="phoneNumberZone">
      <Heading variant={EHeadingStyleVariant.H6} className="mb-4">
        <FormattedMessage defaultMessage="Phone number" id="jdJhOL" />
      </Heading>
      <Controller
        name={EPersonalInfoFields.PhoneNumber}
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Phone number is required',
            id: 'UHu1T/',
          }),
          pattern: {
            value: /^ *(\+?\d{1,3}( |-)*)?(\(\d{1,3}\)( |-)*)?((\d+( |-)+)*\d+)( *x(\d+))? *$/,
            message: intl.formatMessage({
              defaultMessage: 'Please enter a valid phone number.',
              id: 'H+RqPP',
            }),
          },
          minLength: {
            value: 7,
            message: intl.formatMessage({
              defaultMessage: 'Phone number must be at least 7 characters long.',
              id: 'rmJKZ0',
            }),
          },
          maxLength: {
            value: 30,
            message: intl.formatMessage({
              defaultMessage: 'Phone number must be less than 30 characters long.',
              id: 'nQpgOc',
            }),
          },
        }}
        render={({ field: { value, onChange } }) => (
          <TextInput
            controlled
            inputValue={String(value) || ''}
            onChange={onChange}
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Phone number input field',
              id: 'k3+Ybw',
            })}
            label={intl.formatMessage({
              defaultMessage: 'Phone number',
              id: 'jdJhOL',
            })}
            error={!!errors.phoneNumber}
            helpText={errors.phoneNumber?.message}
          />
        )}
      />
      {userData.profile.phone && !userData.verifications.phone && phoneCodeError && (
        <Button
          aria-label={intl.formatMessage({
            defaultMessage: 'Send verification code button',
            id: 'td6rSI',
          })}
          label={intl.formatMessage({
            defaultMessage: 'Send verification code',
            id: 'KFpaMd',
          })}
          className="mt-2 md:w-fit"
          onClick={resendCode}
          loading={isVerifyingPhone}
          variant={EButtonColorVariant.Secondary}
        />
      )}
      {shouldShowValidateAction && (
        <div className="mt-2">
          <Text variant={ETextStyleVariant.LargeBold}>
            <FormattedMessage defaultMessage="Verify your phone number" id="QofabW" />
          </Text>
          <Text className="mt-0.5">
            <FormattedMessage
              defaultMessage="We've sent a 4-digit code to the mobile number above. Please enter the code to change your phone number. It may take a minute to arrive."
              id="qwxXCI"
            />
          </Text>
          {!isFinalizingPhoneCode ? (
            <div className="flex mt-3 mb-10 gap-2">
              <Controller
                name={EPersonalInfoFields.VerifyPhoneNumber1}
                control={control}
                rules={{ min: 0, maxLength: 1 }}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      controlled
                      ref={numberRefs.pn1}
                      label=""
                      ariaLabel={intl.formatMessage({
                        defaultMessage: 'Verify phone number input 1',
                        id: 'Yp6f9d',
                      })}
                      onlyValue={true}
                      onlyValueTextAlignment={ETextInputAlignment.Center}
                      inputValue={value}
                      onChange={val => {
                        if (val.length >= 1) {
                          numberRefs.pn2.current!.focus();
                        }
                        const newVal = val.replace(value, '');

                        if (val.length > 1) {
                          onChange(newVal);
                        } else {
                          onChange(val);
                        }
                      }}
                      type={ETextInputTypes.Number}
                      className="h-[4.25rem] w-[4.25rem] md:h-8 md:w-16"
                    />
                  );
                }}
              />
              <Controller
                name={EPersonalInfoFields.VerifyPhoneNumber2}
                control={control}
                rules={{ min: 0, maxLength: 1 }}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      controlled
                      ref={numberRefs.pn2}
                      label=""
                      ariaLabel={intl.formatMessage({
                        defaultMessage: 'Verify phone number input 2',
                        id: 'gtPI3d',
                      })}
                      onlyValue={true}
                      onlyValueTextAlignment={ETextInputAlignment.Center}
                      inputValue={value}
                      onChange={val => {
                        if (val.length >= 1) {
                          numberRefs.pn3.current!.focus();
                        }
                        const newVal = val.replace(value, '');

                        if (val.length > 1) {
                          onChange(newVal);
                        } else {
                          onChange(val);
                        }
                      }}
                      type={ETextInputTypes.Number}
                      className="h-[4.25rem] w-[4.25rem] md:h-8 md:w-16"
                    />
                  );
                }}
              />
              <Controller
                name={EPersonalInfoFields.VerifyPhoneNumber3}
                control={control}
                rules={{ min: 0, maxLength: 1 }}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      controlled
                      ref={numberRefs.pn3}
                      label=""
                      ariaLabel={intl.formatMessage({
                        defaultMessage: 'Verify phone number input 3',
                        id: 'jTnEnF',
                      })}
                      onlyValue={true}
                      onlyValueTextAlignment={ETextInputAlignment.Center}
                      inputValue={value}
                      onChange={val => {
                        if (val.length >= 1) {
                          numberRefs.pn4.current!.focus();
                        }
                        const newVal = val.replace(value, '');

                        if (val.length > 1) {
                          onChange(newVal);
                        } else {
                          onChange(val);
                        }
                      }}
                      type={ETextInputTypes.Number}
                      className="h-[4.25rem] w-[4.25rem] md:h-8 md:w-16"
                    />
                  );
                }}
              />
              <Controller
                name={EPersonalInfoFields.VerifyPhoneNumber4}
                control={control}
                rules={{ min: 0, maxLength: 1 }}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      controlled
                      ref={numberRefs.pn4}
                      label=""
                      ariaLabel={intl.formatMessage({
                        defaultMessage: 'Verify phone number input 4',
                        id: 'nUxBPP',
                      })}
                      onlyValue={true}
                      onlyValueTextAlignment={ETextInputAlignment.Center}
                      inputValue={value}
                      onChange={val => {
                        const newVal = val.replace(value, '');

                        if (val.length > 1) {
                          onChange(newVal);
                        } else {
                          onChange(val);
                        }
                        submitPhoneCode();
                      }}
                      type={ETextInputTypes.Number}
                      className="h-[4.25rem] w-[4.25rem] md:h-8 md:w-16"
                    />
                  );
                }}
              />
            </div>
          ) : (
            <Loader />
          )}
          <Text variant={ETextStyleVariant.SmallRegular}>
            <FormattedMessage
              defaultMessage="Didn't receive the code? Request to {resendCode}"
              id="g62PK4"
              values={{
                resendCode: (
                  <InlineLink variant={ELinkVariant.Underline} onClick={resendCode}>
                    <FormattedMessage defaultMessage="resend code." id="tvJRPV" />
                  </InlineLink>
                ),
              }}
            />
          </Text>
        </div>
      )}
    </div>
  );
};
